import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { Get } from "libs/services";
import { STORES } from "libs/config";
import { setLoading } from "./loader";
import { StoreItemType } from "src/common/types/response/stores";
import { BaseResponse } from "src/common/types/response/BasePaginationResponse";
import { TOP_STORE_RATING } from "src/common/libs/utils/constants";
import { processPaginatedItems } from "src/common/libs/utils/items";
import { reportLog } from "src/common/libs/utils/logs";

interface ItemsState {
  data: BaseResponse<StoreItemType>;
  loading: boolean;
  page: number;
}

export const getStores = createAsyncThunk(
  "store/getStores",
  async (
    {
      search,
      guest = false,
      page = 1,
      latitude,
      longitude,
    }: {
      search?: string;
      guest: boolean;
      page?: number;
      latitude: string;
      longitude: string;
    },
    thunkAPI
  ) => {
    try {
      const query = search?.length ? { search, page } : { page };
      if (guest) {
        query["role"] = "guest";
      }
      query["latitude"] = latitude;
      query["longitude"] = longitude;
      const response = await Get<BaseResponse<StoreItemType>>(
        STORES.get,
        query
      );

      return { data: response.data, page };
    } catch (error) {
      reportLog({
        error: error,
        location: "features/stores->getStores",
        body: { search, guest, page, latitude, longitude },
      });
      return { data: {} as BaseResponse<StoreItemType>, page };
    } finally {
      thunkAPI.dispatch(setLoading(false));
    }
  }
);

export const getTopStores = createAsyncThunk(
  "store/getTopStores",
  async (
    {
      guest = false,
      rating = TOP_STORE_RATING,
      latitude,
      longitude,
    }: { guest: boolean; rating?: number; latitude: string; longitude: string },
    thunkAPI
  ) => {
    try {
      const query = { rating };
      if (guest) {
        query["role"] = "guest";
      }
      query["latitude"] = latitude;
      query["longitude"] = longitude;
      const response = await Get<BaseResponse<StoreItemType>>(
        STORES.get,
        query
      );

      return response.data;
    } catch (error) {
      reportLog({
        error: error,
        location: "features/stores->getStores",
        body: { guest, rating, latitude, longitude },
      });
      return null;
    } finally {
    }
  }
);

const initialState: ItemsState = {
  data: null,
  loading: false,
  page: 1,
};

export const storeSlice = createSlice({
  name: "stores",
  initialState,
  reducers: {
    clearData: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(getStores.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(getStores.fulfilled, (state, action) => {
      const { data, page } = action.payload || {};
      const filteredPayload = processPaginatedItems(data, state.data);
      if (page === 1) {
        state.data = data;
      } else {
        state.data = {
          ...state.data,
          ...filteredPayload,
        };
      }
      state.page = page;
      state.loading = false;
    });
    builder.addCase(getStores.rejected, (state, action) => {
      state.loading = false;
    });
  },
});

export const { clearData } = storeSlice.actions;

export default storeSlice.reducer;
